import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled from '@mint-lib/styled';
import { getRouteApi } from '@mint/routing';

import { Icon } from '@myn/mui';

import { useIsRedDotShown } from '../../hooks/useGetBuyingPlanData.js';
import { useCampaignDataSelector } from '../../hooks/useGetCampaignData.js';
import useIsBuyingPlanLocked from '../../hooks/useIsBuyingPlanLocked.js';
import DrawerModal, { Tab } from '../../organisms/DrawerModal/DrawerModal.jsx';
import { recommendationsStateActions } from '../../redux/action/api/RecommendationsApi.js';
import { getNewRecommendationsStatusState } from '../../redux/selector/recommendations.js';
import CarbonNeutralPage from '../CarbonNeutralPage/CarbonNeutralPage.jsx';
import RecommendationPanelPage from '../RecommendationPage/RecommendationPanelPage.jsx';
import VersionsPage from '../VersionPage/VersionsPage.jsx';
import { PanelTab } from './PanelTab.js';

type Props = {
  otherTab?: PanelTab | null;
  setOtherTab?: (tab: PanelTab | null) => void;
  mode?: 'normal' | 'recommendation-only';
  tabsBefore?: Tab<any>[];
  tabsAfter?: Tab<any>[];
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const Panel = ({
  otherTab,
  setOtherTab,
  mode = 'normal',
  tabsBefore = [],
  tabsAfter = [],
}: Props) => {
  const { t } = useTranslation('@myn/planning');

  const { tab: tabParam, ...restSearchParams } = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const dispatch = useDispatch();

  const isShowDot = useIsRedDotShown();
  const { data: journeyId } = useCampaignDataSelector('id');

  const { isLocked: isBuyingPlanLocked, isApprovalFlowDataAvailable } =
    useIsBuyingPlanLocked();

  const hasNewRecommendations = useSelector(getNewRecommendationsStatusState);

  const isNonFinancialUser = !useIsAllowedPermission(
    'financialDataFeature:full_control',
  );

  const isCarbonNeutralFeatureAccess = useIsAllowedPermission(
    'carbonNeutralFeature:view',
  );
  const isNotificationsFeatureAccess = useIsAllowedPermission(
    'notificationsFeature:view',
  );

  const handleDrawerClose = useCallback(() => {
    navigate({
      search: {
        ...restSearchParams,
        tab: undefined,
      },
    });
    setOtherTab?.(null);
  }, [restSearchParams]);

  const handleTabChange = (tab: PanelTab | null) => {
    navigate({
      search: {
        ...restSearchParams,
        tab: tab ?? undefined,
      },
    });
  };

  useEffect(() => {
    if (otherTab) {
      navigate({
        search: {
          ...restSearchParams,
          tab: otherTab,
        },
      });
    }
  }, [otherTab]);

  useEffect(() => {
    dispatch(recommendationsStateActions.request(journeyId));
  }, []);

  useEffect(() => {
    if (isBuyingPlanLocked && isApprovalFlowDataAvailable) {
      handleDrawerClose();
    }
  }, [isBuyingPlanLocked, isApprovalFlowDataAvailable]);

  const isSuggestionsTabHidden =
    isBuyingPlanLocked || !isNotificationsFeatureAccess;

  const isCarbonOffsetTabHidden =
    isNonFinancialUser || isBuyingPlanLocked || !isCarbonNeutralFeatureAccess;

  const isVersioningTabHidden = isBuyingPlanLocked;
  const selectedTab = !isBuyingPlanLocked && tabParam ? tabParam : null;
  const showOverlay = !!selectedTab && selectedTab !== PanelTab.AddRow;

  const tabs = [
    ...tabsBefore,
    {
      icon: (
        <span aria-label="Suggestions-icon">
          <Icon
            component="Bot"
            fontSize="large"
            aria-label="open suggestions tab"
          />
        </span>
      ),
      title: t('Suggestions'),
      tooltip: t('Suggestions'),
      value: PanelTab.Recommendations,
      isHidden: isSuggestionsTabHidden,
      isNewData: hasNewRecommendations,
      content: (
        <RecommendationPanelPage onClosePanel={() => handleTabChange(null)} />
      ),
    },
    {
      icon: (
        <span aria-label="Carbon neutral">
          <Icon component="Leaf" fontSize="large" />
        </span>
      ),
      title: t('Carbon Offset'),
      tooltip: t('Carbon Offset'),
      value: PanelTab.Co2,
      content: <CarbonNeutralPage />,
      dot: <RedDot isShowDot={isShowDot || false} />,
      isHidden: isCarbonOffsetTabHidden,
    },
    {
      icon: (
        <span aria-label="Versions">
          <Icon component="RecentlyViewed" fontSize="large" />
        </span>
      ),
      title: t('Buying plan versions'),
      value: PanelTab.Versions,
      tooltip: t('Versioning'),
      content: <VersionsPage handleDrawerClose={handleDrawerClose} />,
      isHidden: isVersioningTabHidden,
    },
    ...tabsAfter,
  ].filter((tab) => {
    if (mode === 'recommendation-only') {
      return tab.value === PanelTab.Recommendations;
    }

    return !!tab;
  });

  return (
    <>
      <Overlay $isActive={showOverlay} onClick={handleDrawerClose} />
      <DrawerModal
        tabs={tabs}
        value={selectedTab}
        onChange={handleTabChange}
        onClose={handleDrawerClose}
        tabsWithOverlay={[
          PanelTab.Co2,
          PanelTab.Recommendations,
          PanelTab.Versions,
        ]}
      />
    </>
  );
};

const RedDot = styled('div')<{ isShowDot: boolean }>`
  display: ${({ isShowDot }) => (isShowDot ? 'block' : 'none')};
  position: absolute;
  width: 11px;
  height: 11px;
  background-color: #fe566a;
  border-radius: 50%;
  top: 1px;
  right: 2px;
`;

const Overlay = styled('div')<{ $isActive: boolean }>`
  position: fixed;
  z-index: 99;
  top: 60px;
  left: 56px;
  right: 0;
  bottom: 0;
  background-color: black;
  pointer-events: ${({ $isActive }) => ($isActive ? 'inherit' : 'none')};
  opacity: ${({ $isActive }) => ($isActive ? 0.1 : 0)};
  transition: opacity ease 0.3s;
`;

export default Panel;
